import { inject } from '@angular/core';
import { ActivityEntityName, EntityViewDisplay, FeatureFlags, FinanceSecurityRight, PurchaseOrder, SecurityModuleTag, SurfaceType } from '@jms/model';
import { RightsService } from '@jms/utils/util.permissions';
import { registerManifest } from '@workbuddy/services/entities/entity.manifest.registry';
import { entityRecordUrl } from '@workbuddy/ui/v2/blocks/entity.record';
import { purchaseOrderStatusTag } from './presentation/purchase.order.status.colour';

// Registered when this manifest module is loaded at bootstrap. Consumers resolve manifests via
// getManifest/EntityManifestService only.
// This is the Tenant manifest; portals get no Purchase Order UI until declared under `domains`.
export const purchaseOrderManifest = registerManifest<PurchaseOrder>({
  entity: ActivityEntityName.PurchaseOrder,
  slug: 'purchase-orders',
  featureFlag: FeatureFlags.FinanceDocsPurchaseOrderV2,
  menu: {
    group: 'finance',
    label: 'Purchase Orders',
    icon: 'fa-shopping-cart',
    order: 40,
    badge: SecurityModuleTag.Beta,
    authorize: () => inject(RightsService).hasRights(FinanceSecurityRight.CanAccessPurchaseOrder),
  },

  routes: {
    root: '/finance/purchase-orders',
    record: id => entityRecordUrl('purchase-orders', 'preview', id),
  },

  // tags feeds the BOARD CARD's status badge; the surface header reads the same colours
  // through the same purchaseOrderStatusTag function, so card and header cannot drift apart.
  presentation: {
    tags: {
      status: purchaseOrderStatusTag,
    },
  },

  board: {
    // Kanban is not meaningful for financial documents; locked manage preview is a per-view toggle.
    supportedDisplays: [EntityViewDisplay.Card, EntityViewDisplay.Table],
    // Document-shaped: the selection shows in the wide ~50/50 aside, not a narrow quickview.
    panel: {
      kind: 'preview',
    },
    defaults: {
      header: {
        title: 'Purchase Orders',
      },
    },
  },

  surfaces: {
    [SurfaceType.FinancialDocument]: () => import('./surface/manage/purchase.order.manage.surface').then(m => m.purchaseOrderSurface),
  },

  operations: {
    preview: {
      surface: SurfaceType.FinancialDocument,
      authorize: ({ injector }) => injector.get(RightsService).hasRights(FinanceSecurityRight.CanAccessPurchaseOrder),
    },
    create: {
      surface: SurfaceType.FinancialDocument,
      authorize: ({ injector }) => injector.get(RightsService).hasRights(FinanceSecurityRight.ManagePurchaseOrder),
    },
    // Read access mounts the document; write rights, status, and approval belong to the surface.
    edit: {
      surface: SurfaceType.FinancialDocument,
      authorize: ({ injector }) => injector.get(RightsService).hasRights(FinanceSecurityRight.CanAccessPurchaseOrder),
    },
  },

  relations: {
    [ActivityEntityName.Activity]: () => import('./relations/purchase.order.activities.relation').then(m => m.purchaseOrderActivitiesBoardBuilder),
  },
});
